import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Api from "../../../shared/api/api";
import { getStoredOrLatestTestId } from "../helpers/fourHTest";

import MessageModal from "./MessageModal";

import "../styles/UserIDInput.css";

const INITIAL_FORM_DATA = {
  fourHID: "",
};

const buildFullName = (user) =>
  [
    user?.name_first,
    user?.name_middle,
    user?.name_last,
  ]
    .filter(Boolean)
    .join(" ");

export default function UserIDInput() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(
    INITIAL_FORM_DATA
  );

  const [user, setUser] = useState(null);
  const [testId, setTestId] = useState(null);

  const [step, setStep] = useState("id");
  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadTestId = async () => {
      try {
        const latestTestId =
          await getStoredOrLatestTestId();

        if (isMounted) {
          setTestId(latestTestId);
        }
      } catch (requestError) {
        console.error(
          "Unable to load the current test ID:",
          requestError
        );

        if (isMounted) {
          setError(
            "The current test could not be loaded."
          );
        }
      }
    };

    loadTestId();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (event) => {
    setFormData({
      fourHID: event.target.value,
    });

    setError("");
  };

  const handleIdSubmit = async (event) => {
    event.preventDefault();

    const userId = formData.fourHID.trim();

    if (!userId) {
      setError("Please enter your 4-H ID.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const currentTestId =
        testId ||
        (await getStoredOrLatestTestId());

      if (!currentTestId) {
        throw new Error(
          "No active test was found."
        );
      }

      const userInfo =
        await Api.getUserById(userId);

      if (
        userInfo?.error ||
        !userInfo?.four_h_id
      ) {
        throw new Error(
          userInfo?.error ||
            "The entered 4-H ID was not found."
        );
      }

      const assessmentStatus =
        await Api.getUserAssessment(
          userId,
          currentTestId
        );

      setUser(userInfo);
      setTestId(currentTestId);

      if (assessmentStatus?.error) {
        setStep("completed");
      } else {
        setStep("confirm");
      }
    } catch (requestError) {
      console.error(
        "Unable to verify FourH user:",
        requestError
      );

      setError(
        requestError?.message ||
          "The entered ID could not be verified."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmUser = () => {
    if (!user) {
      setError(
        "The participant information is unavailable."
      );
      setStep("id");
      return;
    }

    if (user.coordinator) {
      navigate(
        `/fourh/coordinator/${user.four_h_id}`
      );
      return;
    }

    if (user.admin) {
      navigate(
        `/fourh/admin/${user.four_h_id}`
      );
      return;
    }

    setStep("agreement");
  };

  const handleRejectUser = () => {
    setUser(null);
    setFormData(INITIAL_FORM_DATA);
    setError("");
    setStep("id");
  };

  const agreementMessage = (
    <>
      <span>
        By using this system, you agree that:
      </span>

      <ul>
        <li>
          You will not use Google, Bing, or
          another search engine to find answers.
        </li>

        <li>
          You will not navigate away from the test
          after it begins.
        </li>
      </ul>

      <span>
        Performing either forbidden action may
        submit the test and result in a score of
        zero.
      </span>
    </>
  );

  return (
    <div className="userIDContainer">
      {step === "agreement" && user ? (
        <div className="userIDMessageModalWrapper">
          <MessageModal
            header="Please acknowledge the statement below"
            message={agreementMessage}
            url={`/fourh/test/${user.four_h_id}`}
            buttonText="Agree and Begin"
          />
        </div>
      ) : step === "id" ? (
        <div className="allContent">
          {error && (
            <div
              className="warningError"
              role="alert"
            >
              <p>{error}</p>
            </div>
          )}

          <h2 className="userIDTitle">
            Please Enter Your 4-H ID
          </h2>

          <form
            className="userIDForm"
            onSubmit={handleIdSubmit}
          >
            <div className="userIDDiv">
              <label
                className="sr-only"
                htmlFor="fourHID"
              >
                4-H ID
              </label>

              <input
                type="text"
                className="userIDInput"
                placeholder="4-H ID"
                name="fourHID"
                id="fourHID"
                required
                value={formData.fourHID}
                onChange={handleChange}
                autoComplete="off"
              />
            </div>

            <div className="userIDSubmitBtn">
              <button
                type="submit"
                className="fourHUserIDSubmitBtn"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Checking..."
                  : "Submit"}
              </button>
            </div>
          </form>
        </div>
      ) : step === "completed" ? (
        <div
          className="warningError"
          role="status"
        >
          <p>
            You have already taken this exam. There
            is nothing else for you to do.
          </p>

          <button
            type="button"
            className="confirmationNo"
            onClick={handleRejectUser}
          >
            Return
          </button>
        </div>
      ) : (
        <section className="userConfirmationModal">
          <h1 className="confirmationTitle">
            Please Confirm You Are the Participant
            Below
          </h1>

          <p className="confirmationInfo">
            <strong>ID:</strong>{" "}
            {user?.four_h_id}
          </p>

          <p className="confirmationInfo">
            <strong>Name:</strong>{" "}
            {buildFullName(user)}
          </p>

          <p className="confirmationInfo">
            <strong>Level:</strong>{" "}
            {user?.level || "Not assigned"}
          </p>

          <p className="confirmationInfo">
            <strong>Team:</strong>{" "}
            {user?.team || "Not assigned"}
          </p>

          <div className="confirmationTextContainer">
            <h2 className="confirmationText">
              Is your information correct?
            </h2>
          </div>

          <div className="buttons">
            <div className="btnContainer">
              <button
                type="button"
                className="confirmationYes"
                onClick={handleConfirmUser}
              >
                Yes
              </button>
            </div>

            <div className="btnContainer">
              <button
                type="button"
                className="confirmationNo"
                onClick={handleRejectUser}
              >
                No
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}